/**
 * Devoluciones (Returns) — admin module.
 *
 * Lets the admin:
 *   • Browse all previously-issued returns + their generated credit notes.
 *   • Create a new return: pick an invoice → pick which lines to return →
 *     enter cantidades → record motivo. Backend auto-creates the NC,
 *     applies it against the source invoice and writes stock movements
 *     (entrada) so inventory is restocked.
 *
 * Premium+ feature (rides on the existing `receivables` plan gating
 * because returns + credit notes only make sense if the user can issue
 * invoices and track CxC).
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Undo2, Receipt, ArrowLeft, Plus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { PageLoader } from "@/components/Spinner";

const formatDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("es-CR", { dateStyle: "short" }); }
  catch { return iso; }
};

export default function Returns() {
  const [loading, setLoading] = useState(true);
  const [returns, setReturns] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("returns");

  // New return wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = pick invoice, 2 = pick lines, 3 = confirm
  const [invoices, setInvoices] = useState([]);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [returnLines, setReturnLines] = useState([]); // [{codigo, producto, max_cantidad, cantidad, precio_unitario}]
  const [motivo, setMotivo] = useState("");
  const [attachment, setAttachment] = useState(null); // File | null
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    try {
      const [rs, ncs] = await Promise.all([
        api.get("/returns"),
        api.get("/credit-notes"),
      ]);
      setReturns(rs.data);
      setCreditNotes(ncs.data);
    } catch { toast.error("Error al cargar devoluciones"); }
  };

  useEffect(() => { setLoading(true); fetchAll().finally(() => setLoading(false)); }, []);

  const filteredReturns = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return returns;
    return returns.filter(r =>
      (r.numero || "").toLowerCase().includes(q) ||
      (r.nombre_cliente || "").toLowerCase().includes(q) ||
      (r.invoice_numero || "").toLowerCase().includes(q),
    );
  }, [returns, search]);

  const filteredNCs = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return creditNotes;
    return creditNotes.filter(n =>
      (n.numero || "").toLowerCase().includes(q) ||
      (n.nombre_cliente || "").toLowerCase().includes(q) ||
      (n.invoice_numero || "").toLowerCase().includes(q),
    );
  }, [creditNotes, search]);

  // ---- KPIs ----
  const totalReturned = useMemo(() => returns.reduce((s, r) => s + (r.total || 0), 0), [returns]);
  const ncActive = useMemo(() => creditNotes.filter(n => n.estado === "activa" && (n.saldo_disponible || 0) > 0), [creditNotes]);
  const ncSaldo = useMemo(() => ncActive.reduce((s, n) => s + (n.saldo_disponible || 0), 0), [ncActive]);

  // ---- Wizard ----
  const openWizard = async () => {
    setStep(1);
    setSelectedInvoice(null);
    setReturnLines([]);
    setMotivo("");
    setAttachment(null);
    setInvoiceSearch("");
    try {
      // Load emitida invoices (not yet returned). Reuse /invoices endpoint.
      const { data } = await api.get("/invoices");
      // Filter to non-cancelled (we still allow returning partially-paid ones).
      setInvoices((data || []).filter(i => (i.estado || "").toLowerCase() !== "anulada"));
    } catch { setInvoices([]); }
    setWizardOpen(true);
  };

  const pickInvoice = (inv) => {
    setSelectedInvoice(inv);
    // Pre-fill the lines with cantidad=0 so admin chooses what to return.
    setReturnLines((inv.lineas || []).map(l => ({
      codigo: l.codigo,
      producto: l.producto || l.descripcion || "",
      precio_unitario: l.precio_unitario || 0,
      max_cantidad: l.cantidad || 0,
      cantidad: 0,
    })));
    setStep(2);
  };

  const updateLineQty = (idx, val) => {
    const num = parseFloat(val);
    setReturnLines(prev => prev.map((l, i) => i === idx
      ? { ...l, cantidad: isNaN(num) ? 0 : Math.max(0, Math.min(num, l.max_cantidad)) }
      : l));
  };

  const wizardTotal = useMemo(() => returnLines.reduce(
    (s, l) => s + (l.cantidad || 0) * (l.precio_unitario || 0), 0,
  ), [returnLines]);

  const submitReturn = async () => {
    const lineas = returnLines
      .filter(l => (l.cantidad || 0) > 0)
      .map(l => ({ codigo: l.codigo, cantidad: l.cantidad, motivo_linea: "" }));
    if (lineas.length === 0) { toast.error("Indicá cantidad > 0 en al menos una línea"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/returns", {
        invoice_id: selectedInvoice.id,
        lineas,
        motivo: motivo || "",
      });
      // Upload optional backing document (acta firmada / foto del daño).
      if (attachment) {
        try {
          const fd = new FormData();
          fd.append("file", attachment);
          await api.post(`/returns/${data.id}/attachment`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (uerr) {
          toast.error("Devolución creada pero el adjunto falló: " + (uerr.response?.data?.detail || ""));
        }
      }
      toast.success(`Devolución ${data.numero} creada · NC ${data.credit_note_numero}`);
      setWizardOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear devolución");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoader label="Cargando devoluciones…" testid="returns-loader" />;

  const filteredInvoices = invoices.filter(i => {
    const q = invoiceSearch.toLowerCase().trim();
    if (!q) return true;
    return (i.numero || "").toLowerCase().includes(q) ||
           (i.nombre_cliente || "").toLowerCase().includes(q);
  });

  return (
    <div data-testid="returns-page">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Devoluciones
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Cada devolución repone stock al inventario, emite una Nota de Crédito y reduce la deuda del cliente en Cuentas por Cobrar.
          </p>
        </div>
        <Button onClick={openWizard} className="btn-primary rounded-sm" data-testid="returns-new-button">
          <Plus className="h-4 w-4 mr-2" /> Nueva devolución
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
        <Card className="bg-white border border-rose-200 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">DEVUELTO ACUMULADO</p>
            <p className="text-2xl font-black text-rose-700" data-testid="returns-total">{formatMoney(totalReturned)}</p>
          </div>
          <Undo2 className="h-6 w-6 text-rose-600" />
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">DEVOLUCIONES</p>
            <p className="text-2xl font-black">{returns.length}</p>
          </div>
          <Receipt className="h-6 w-6 text-[rgb(0,47,167)]" />
        </Card>
        <Card className="bg-white border border-emerald-200 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">SALDO NC A FAVOR</p>
            <p className="text-2xl font-black text-emerald-700">{formatMoney(ncSaldo)}</p>
          </div>
          <Receipt className="h-6 w-6 text-emerald-600" />
        </Card>
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {[
          { key: "returns", label: `Devoluciones (${returns.length})` },
          { key: "ncs",     label: `Notas de crédito (${creditNotes.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? "border-[rgb(0,47,167)] text-[rgb(0,47,167)]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`returns-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === "returns" ? "Buscar por número, cliente o factura…" : "Buscar NC por número, cliente o factura origen…"}
            className="rounded-sm pl-9" data-testid="returns-search" />
        </div>

        {tab === "returns" ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-slate-700">N° Devolución</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Fecha</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Cliente</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Factura origen</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">NC generada</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Líneas</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Total</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Motivo</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Adjunto</th>
                </tr>
              </thead>
              <tbody>
                {filteredReturns.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100" data-testid={`return-row-${r.numero}`}>
                    <td className="py-2 px-3 mono font-medium">{r.numero}</td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{formatDate(r.fecha)}</td>
                    <td className="py-2 px-3">{r.nombre_cliente}</td>
                    <td className="py-2 px-3 mono text-slate-600">{r.invoice_numero || "-"}</td>
                    <td className="py-2 px-3 mono text-emerald-700">{r.credit_note_numero}</td>
                    <td className="py-2 px-3 text-slate-600">{(r.lineas || []).length}</td>
                    <td className="py-2 px-3 text-right font-bold mono text-rose-700">{formatMoney(r.total)}</td>
                    <td className="py-2 px-3 text-slate-600 max-w-[260px] truncate" title={r.motivo}>{r.motivo || "-"}</td>
                    <td className="py-2 px-3">
                      {r.motivo_url ? (
                        <div className="flex items-center gap-1">
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${r.motivo_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-[rgb(0,47,167)] hover:underline"
                            data-testid={`return-attachment-${r.numero}`}
                          >
                            Ver adjunto
                          </a>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm("¿Eliminar el archivo adjunto de esta devolución?")) return;
                              try {
                                await api.delete(`/returns/${r.id}/attachment`);
                                toast.success("Adjunto eliminado");
                                fetchAll();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "No se pudo eliminar");
                              }
                            }}
                            className="p-1 rounded-sm text-rose-600 hover:bg-rose-50"
                            title="Eliminar adjunto"
                            aria-label="Eliminar adjunto"
                            data-testid={`return-attachment-delete-${r.numero}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
                {filteredReturns.length === 0 && (
                  <tr><td colSpan={9} className="py-10 text-center text-slate-400">Sin devoluciones registradas.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-slate-700">N° NC</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Fecha</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Cliente</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Origen</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Factura</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Monto</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Saldo disponible</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Estado</th>
                </tr>
              </thead>
              <tbody>
                {filteredNCs.map((n) => (
                  <tr key={n.id} className="border-b border-slate-100" data-testid={`nc-row-${n.numero}`}>
                    <td className="py-2 px-3 mono font-medium">{n.numero}</td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{formatDate(n.fecha)}</td>
                    <td className="py-2 px-3">{n.nombre_cliente}</td>
                    <td className="py-2 px-3 text-slate-600">{n.origen}</td>
                    <td className="py-2 px-3 mono text-slate-600">{n.invoice_numero || "-"}</td>
                    <td className="py-2 px-3 text-right font-bold mono">{formatMoney(n.monto)}</td>
                    <td className="py-2 px-3 text-right mono">{n.saldo_disponible > 0 ? <span className="text-emerald-700 font-medium">{formatMoney(n.saldo_disponible)}</span> : "-"}</td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${
                        n.estado === "activa" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                        n.estado === "consumida" ? "bg-slate-100 text-slate-700 border-slate-200" :
                        "bg-rose-50 text-rose-800 border-rose-200"
                      }`}>{n.estado}</span>
                    </td>
                  </tr>
                ))}
                {filteredNCs.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-400">Sin notas de crédito.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* New return wizard */}
      <Dialog open={wizardOpen} onOpenChange={(o) => !o && setWizardOpen(false)}>
        <DialogContent className="max-w-3xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-[rgb(0,47,167)]" />
              Nueva devolución · Paso {step} de 2
            </DialogTitle>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Elegí la factura que querés devolver.</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={invoiceSearch} onChange={(e) => setInvoiceSearch(e.target.value)}
                  placeholder="Buscar por número o cliente…"
                  className="rounded-sm pl-9" data-testid="returns-wizard-search" />
              </div>
              <div className="border border-slate-200 rounded-sm max-h-[50vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium">Número</th>
                      <th className="text-left py-2 px-3 font-medium">Fecha</th>
                      <th className="text-left py-2 px-3 font-medium">Cliente</th>
                      <th className="text-right py-2 px-3 font-medium">Total</th>
                      <th className="py-2 px-3 w-px"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.slice(0, 100).map(inv => (
                      <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`returns-pick-invoice-${inv.numero || inv.id.substring(0,8)}`}>
                        <td className="py-1.5 px-3 mono">{inv.numero || `#${inv.id.substring(0,8)}`}</td>
                        <td className="py-1.5 px-3 text-slate-600 whitespace-nowrap">{formatDate(inv.fecha || inv.created_at)}</td>
                        <td className="py-1.5 px-3">{inv.nombre_cliente}</td>
                        <td className="py-1.5 px-3 text-right mono font-bold">{formatMoney(inv.total)}</td>
                        <td className="py-1.5 px-3">
                          <Button size="sm" className="rounded-sm h-7" onClick={() => pickInvoice(inv)}>Elegir</Button>
                        </td>
                      </tr>
                    ))}
                    {filteredInvoices.length === 0 && (
                      <tr><td colSpan={5} className="py-10 text-center text-slate-400">No encontramos facturas.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 2 && selectedInvoice && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="rounded-sm">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Cambiar factura
                </Button>
                <span className="text-sm text-slate-600">
                  Factura <strong>{selectedInvoice.numero}</strong> · {selectedInvoice.nombre_cliente}
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Indicá la cantidad a devolver de cada línea (máximo lo facturado). El stock se repondrá automáticamente.
              </p>
              <div className="border border-slate-200 rounded-sm max-h-[40vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium">Código</th>
                      <th className="text-left py-2 px-3 font-medium">Producto</th>
                      <th className="text-right py-2 px-3 font-medium">P/U</th>
                      <th className="text-right py-2 px-3 font-medium">Facturado</th>
                      <th className="text-right py-2 px-3 font-medium w-32">Devolver</th>
                      <th className="text-right py-2 px-3 font-medium">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returnLines.map((l, idx) => (
                      <tr key={`${l.codigo}-${idx}`} className="border-t border-slate-100" data-testid={`returns-wizard-line-${idx}`}>
                        <td className="py-1.5 px-3 mono">{l.codigo}</td>
                        <td className="py-1.5 px-3">{l.producto}</td>
                        <td className="py-1.5 px-3 text-right mono text-slate-600">{formatMoney(l.precio_unitario)}</td>
                        <td className="py-1.5 px-3 text-right mono text-slate-600">{l.max_cantidad}</td>
                        <td className="py-1.5 px-3">
                          <Input
                            type="number"
                            min={0}
                            max={l.max_cantidad}
                            step="any"
                            value={l.cantidad || ""}
                            onChange={(e) => updateLineQty(idx, e.target.value)}
                            placeholder="0"
                            className="rounded-sm text-right h-8"
                            data-testid={`returns-wizard-qty-${idx}`}
                          />
                        </td>
                        <td className="py-1.5 px-3 text-right mono font-bold">{formatMoney((l.cantidad || 0) * (l.precio_unitario || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Motivo (opcional)</label>
                <Textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Producto vencido, error de despacho, daño durante transporte…"
                  className="rounded-sm mt-1"
                  rows={2}
                  data-testid="returns-wizard-motivo"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Adjunto (opcional)</label>
                <p className="text-[11px] text-slate-500 mb-1.5">Acta firmada por el cliente o foto del daño. PDF / PNG / JPG hasta 10 MB.</p>
                <Input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                  className="rounded-sm"
                  data-testid="returns-wizard-attachment"
                />
                {attachment && (
                  <p className="text-[11px] text-emerald-700 mt-1" data-testid="returns-wizard-attachment-name">
                    ✓ {attachment.name} · {(attachment.size / 1024).toFixed(0)} KB
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <span className="text-sm text-slate-600">Total a devolver (incluye IVA recalculado)</span>
                <span className="text-xl font-black text-rose-700" data-testid="returns-wizard-total">{formatMoney(wizardTotal)}</span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" className="rounded-sm" onClick={() => setWizardOpen(false)}>Cancelar</Button>
            {step === 2 && (
              <Button
                onClick={submitReturn}
                disabled={saving || wizardTotal <= 0}
                className="btn-primary rounded-sm"
                data-testid="returns-wizard-submit"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Undo2 className="h-4 w-4 mr-2" />}
                Crear devolución
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
